<script setup>
/**
 * Where the link SSO mails to a new address (XN-4561) lands, in the sign-in
 * pages' frame like the password reset. It confirms on arrival and says how
 * that went. It signs nobody in or out: a session the person already has on
 * this device carries on, and "Sign in" is for everyone else.
 *
 * Each answer is in the hub's own words, never the server's message.
 *
 * The token is the credential: the route's guard (helpers/confirmEmailLink)
 * has already taken it out of the address bar and turned the Referer off,
 * before the sign-in layout asked the server for anything.
 */
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import PgeButton from '../../../pge-components/pge-button.vue';
import Loading from '../../Loading.vue';
import Logo from '../../preview/Logo.vue';
import { confirmEmailChange } from '../../../api/portal';
import { releaseReferrer, takeConfirmToken } from '../../../helpers/confirmEmailLink';

defineProps({
  // The hub's sign-in settings, from the layout; false when they could not be read.
  eventData: { type: [Object, Boolean], default: null },
});

const GONE = 'This link has expired or has already been used. You can ask for a new one from My details.';
const SAYS = {
  409: 'That email is now used by another account. Ask for a new link from My details.',
  410: GONE,
};
const AGAIN = "Couldn't confirm just now — try the link again.";

const router = useRouter();
const outcome = ref(null); // null while confirming, then { words, confirmed }
const heading = ref(null);

// Held in memory by the route's guard; read once.
const token = takeConfirmToken();
onBeforeUnmount(releaseReferrer);

async function confirm() {
  // No token, an empty one, or two: a link that cannot work, so nothing is sent.
  if (typeof token !== 'string' || !token) return { words: GONE, confirmed: false };

  try {
    const email = await confirmEmailChange(token);
    return { words: `Your email is now ${email}.`, confirmed: true };
  } catch (error) {
    return { words: SAYS[error?.response?.status] ?? AGAIN, confirmed: false };
  }
}

onMounted(async () => {
  outcome.value = await confirm();
  await nextTick();
  heading.value?.focus();
});
</script>

<template>
  <div class="confirm">
    <div class="confirm__image">
      <Logo :logo="eventData?.settings?.logo || ''" />
    </div>
    <div class="confirm__outcome" aria-live="polite" :aria-busy="outcome ? undefined : 'true'" data-testid="confirm-outcome">
      <Loading v-if="!outcome" variant="lines" :rows="3" />
      <template v-else>
        <h1 ref="heading" class="confirm__title" tabindex="-1">{{ outcome.words }}</h1>
        <div v-if="outcome.confirmed" class="confirm__actions">
          <pge-button label="Sign in" @click="router.push('/login')" />
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
/* The password reset's frame (ResetPassword.vue), so the two read as one family. */
.confirm {
  display: flex;
  flex-direction: column;
  gap: 18px;

  &__image { display: flex; justify-content: center; }
  &__outcome { display: flex; flex-direction: column; gap: 18px; min-height: 48px; }
  &__title {
    margin: 0;
    text-align: center;
    font-weight: 700;
    font-size: 18px;
    line-height: 1.5;
    color: #000;
    overflow-wrap: anywhere;
  }
  &__actions { display: flex; justify-content: center; }
}
</style>
